import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetCourses, useDeleteCourse } from "./hooks";
import { Course, getThumbnailUrl } from "./api";
import { useT } from "../../i18n";

const levelBadge: Record<string, string> = {
  beginner: "bg-green-100 text-green-800",
  intermediate: "bg-yellow-100 text-yellow-800",
  advanced: "bg-orange-100 text-orange-800",
  certification: "bg-purple-100 text-purple-800",
};

const CourseCard: React.FC<{
  course: Course;
  onEdit: (c: Course) => void;
  onDelete: (c: Course) => void;
}> = ({ course, onEdit, onDelete }) => {
  const t = useT();
  const allLessons = (course.sections || []).flatMap((s) => s.lessons || []);
  const firstVideo = allLessons.find((l) => l.videoUrl);
  const thumb =
    course.thumbnailUrl ||
    (firstVideo
      ? getThumbnailUrl(firstVideo.videoUrl || "", firstVideo.source || "")
      : null);
  const lessonCount = allLessons.length;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
        {thumb ? (
          <img src={thumb} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <i className="fa-solid fa-graduation-cap text-4xl text-gray-300"></i>
        )}
        <span
          className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${
            levelBadge[course.level] || "bg-gray-100 text-gray-700"
          }`}
        >
          {course.level}
        </span>
        <span className="absolute top-3 right-3 text-xs font-medium px-2 py-1 rounded-md bg-black/60 text-white">
          {t("academy.lessonCount", { count: lessonCount })}
        </span>
        {course.status === "draft" && (
          <span className="absolute bottom-3 left-3 text-xs font-semibold px-2 py-1 rounded-full bg-gray-800/80 text-white">
            <i className="fa-solid fa-pen-ruler mr-1"></i>{t("academy.draft")}
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <span className="text-xs uppercase tracking-wide text-gray-400">
          {course.category}
        </span>
        <h3 className="text-base font-semibold text-gray-900 line-clamp-1 mt-0.5">
          {course.title}
        </h3>
        {course.description && (
          <p className="mt-1 text-sm text-gray-500 line-clamp-2">
            {course.description}
          </p>
        )}
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={() => onEdit(course)}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <i className="fa-solid fa-pen mr-1.5"></i> {t("common.edit")}
          </button>
          <button
            onClick={() => onDelete(course)}
            className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
          >
            <i className="fa-solid fa-trash mr-1.5"></i> {t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
    <div className="aspect-video bg-gray-200" />
    <div className="p-4 space-y-3">
      <div className="h-3 bg-gray-200 rounded w-1/3" />
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-full" />
    </div>
  </div>
);

export default function AcademyPage() {
  const navigate = useNavigate();
  const t = useT();
  const { data: courses = [], isLoading, error } = useGetCourses();
  const deleteCourse = useDeleteCourse();

  const handleAdd = () => navigate("/academy/new");
  const handleEdit = (c: Course) => navigate(`/academy/${c.id}/edit`);
  const handleDelete = (c: Course) => {
    if (window.confirm(t("academy.confirmDelete", { title: c.title }))) deleteCourse.mutate(c.id);
  };

  // Group courses by category.
  const grouped: Record<string, Course[]> = {};
  (courses as Course[]).forEach((c) => {
    (grouped[c.category] = grouped[c.category] || []).push(c);
  });

  return (
    <div className="flex flex-col flex-1 h-full bg-gray-50 min-h-screen">
      <div className="bg-white px-4 sm:px-8 pt-5 pb-5 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sticky top-0 z-20">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t("academy.title")}</h1>
          <p className="text-xs sm:text-sm text-gray-500 max-w-2xl">
            {t("academy.subtitle")}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2.5 flex items-center justify-center bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm font-semibold shadow-sm w-full sm:w-auto"
        >
          <i className="fa-solid fa-plus mr-2"></i> {t("academy.addCourse")}
        </button>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-6">
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {error && (
          <div className="bg-white rounded-2xl border border-red-200 p-8 text-center text-sm text-red-600">
            {t("academy.loadFailed")}
          </div>
        )}

        {!isLoading && !error && (courses as Course[]).length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-brand-50 flex items-center justify-center">
              <i className="fa-solid fa-graduation-cap text-2xl text-brand-500"></i>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-800">
              {t("academy.empty.title")}
            </h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              {t("academy.empty.body")}
            </p>
            <button
              onClick={handleAdd}
              className="mt-5 px-4 py-2.5 inline-flex items-center bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm font-semibold"
            >
              <i className="fa-solid fa-plus mr-2"></i> {t("academy.addCourse")}
            </button>
          </div>
        )}

        {!isLoading &&
          !error &&
          Object.entries(grouped).map(([category, list]) => (
            <div key={category} className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {category}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {list.map((c) => (
                  <CourseCard
                    key={c.id}
                    course={c}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
